<template>
  <div>
    <b-modal
        ref="invoiceModal"
        size="lg"
        centered
        hide-footer
        :title="modalTitle"
        content-class="invoice-modal-content"
        header-class="invoice-modal-header"
        body-class="p-0"
    >
      <!-- Step progress bar -->
      <div class="invoice-progress">
        <div
            v-for="(s, i) in stepLabels"
            :key="i"
            class="invoice-progress-step"
            :class="{
              'step-done':    step > i,
              'step-active':  step === i,
              'step-pending': step < i,
            }"
        >
          <div class="step-bubble">
            <feather-icon v-if="step > i" icon="CheckIcon" size="13"/>
            <span v-else>{{ i + 1 }}</span>
          </div>
          <span class="step-label d-none d-sm-inline">{{ s }}</span>
        </div>
        <div class="progress-line"/>
      </div>

      <!-- Loading overlay -->
      <div v-if="loading.invoice" class="invoice-loading">
        <b-spinner variant="primary" style="width: 2.5rem; height: 2.5rem;"/>
        <p class="mt-1 text-muted mb-0 small">{{ msg('Loading invoice...') }}</p>
      </div>

      <div v-else class="invoice-body">

        <!-- ── STEP 0: Select Expense ── -->
        <div v-show="step === STEPS.SET_EXPENSES">
          <div class="step-section-header">
            <feather-icon icon="FileTextIcon" size="18" class="mr-75 text-primary"/>
            <span>{{ msg('Select the expense period and tax rate') }}</span>
          </div>
          <div class="p-2">
            <b-row>
              <b-col cols="12" md="6">
                <b-form-group :label="msg('Expense period')">
                  <v-select
                      v-show="!useCustomPeriod"
                      v-model="expenseId"
                      :options="expensesOptions"
                      :reduce="o => o.value"
                      label="text"
                      :selectable="expensePeriodSelectable"
                      :placeholder="msg('Select a period...')"
                      @input="onExpensePeriodInput"
                  />
                  <b-row v-show="useCustomPeriod" class="mx-0">
                    <b-col cols="6" class="pl-0 pr-50">
                      <b-form-select
                          v-model="customMonth"
                          :options="customMonthOptions"
                          :placeholder="msg('Month')"
                      />
                    </b-col>
                    <b-col cols="6" class="pr-0 pl-50">
                      <b-form-select
                          v-model="customYear"
                          :options="customYearOptions"
                          :placeholder="msg('Year')"
                      />
                    </b-col>
                  </b-row>
                </b-form-group>
                <b-form-checkbox v-model="useCustomPeriod" class="mb-0 mt-50" @change="onCustomPeriodToggle">
                  {{ msg('Custom month / year') }}
                </b-form-checkbox>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group :label="msg('Tax rate')">
                  <v-select
                      v-model="selectedTax"
                      :options="taxesOptions"
                      :reduce="o => o.value"
                      label="text"
                      :placeholder="msg('No tax (optional)')"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-alert show variant="light" class="info-hint px-1 py-75 mb-2">
              <feather-icon icon="InfoIcon" size="13" class="mr-50 text-info"/>
              <small>{{ msg('Items will be pre-filled from the selected expense period. You can edit them in the next step. Periods with a paid invoice cannot be re-selected; use Custom month / year for a new period.') }}</small>
            </b-alert>

            <b-alert v-if="!useCustomPeriod && expensesOptions.length === 0" show variant="warning" class="mb-2 py-75">
              <small>{{ expensesEmptyMessage }}</small>
            </b-alert>

            <b-button
                block
                variant="primary"
                :disabled="!canContinueFromExpenseStep"
                @click="setItemsByExpenses"
                class="mt-50 btn-invoice-action"
            >
              {{ msg('Continue') }}
              <feather-icon icon="ArrowRightIcon" size="15" class="ml-50"/>
            </b-button>
          </div>
        </div>

        <!-- ── STEP 1: Configure Items ── -->
        <div v-show="step === STEPS.SET_ITEMS">
          <div class="step-section-header">
            <feather-icon icon="EditIcon" size="18" class="mr-75 text-warning"/>
            <span>{{ msg('Configure invoice title and line items') }}</span>
          </div>
          <div class="p-2">
            <b-form-group :label="msg('Invoice title')">
              <b-form-input
                  v-model="invoice.invoiceTitle"
                  :placeholder="msg('e.g. Management Invoice 03/2026')"
              />
            </b-form-group>

            <!-- Items table -->
            <div class="items-table-wrapper">
              <div class="items-table-head">
                <span class="col-name">{{ msg('Item name') }}</span>
                <span class="col-qty">{{ msg('Qty') }}</span>
                <span class="col-price">{{ msg('Price (€)') }}</span>
                <span class="col-total">{{ msg('Total') }}</span>
                <span class="col-del"/>
              </div>

              <draggable
                  v-model="invoice.items"
                  group="invoice-items"
                  handle=".drag-handle"
                  @start="drag=true"
                  @end="drag=false"
                  class="items-list"
              >
                <div
                    class="item-row"
                    v-for="(item, index) in invoice.items"
                    :key="index"
                >
                  <div class="drag-handle">
                    <feather-icon icon="MenuIcon" size="14" class="text-muted"/>
                  </div>
                  <b-form-input
                      class="col-name"
                      v-model="item.name"
                      :placeholder="msg('Item name')"
                      size="sm"
                  />
                  <b-form-input
                      class="col-qty"
                      v-model.number="item.quantity"
                      type="number"
                      min="0"
                      step="1"
                      size="sm"
                  />
                  <b-form-input
                      class="col-price"
                      v-model.number="item.price"
                      type="number"
                      min="0"
                      step="0.01"
                      size="sm"
                  />
                  <span class="col-total text-right font-weight-bold small">
                    {{ formatAmount(item.price * item.quantity) }}
                  </span>
                  <b-button
                      class="col-del btn-icon p-25"
                      variant="flat-danger"
                      size="sm"
                      @click="invoice.items.splice(index, 1)"
                  >
                    <feather-icon icon="Trash2Icon" size="14"/>
                  </b-button>
                </div>
              </draggable>

              <!-- Empty state -->
              <div v-if="invoice.items.length === 0" class="items-empty">
                <feather-icon icon="InboxIcon" size="28" class="text-muted mb-50"/>
                <p class="text-muted small mb-0">{{ msg('No items yet. Add one below.') }}</p>
              </div>

              <!-- Add item row -->
              <div class="items-add-row">
                <b-button variant="flat-primary" size="sm" @click="addItem">
                  <feather-icon icon="PlusIcon" size="14" class="mr-25"/>
                  {{ msg('Add item') }}
                </b-button>
              </div>
            </div>

            <!-- Sub-total hint -->
            <div v-if="invoice.items.length > 0" class="items-subtotal">
              <span class="text-muted small">{{ msg('Subtotal') }}:</span>
              <span class="font-weight-bolder ml-50">{{ formatAmount(itemsSubtotal) }}</span>
            </div>

            <b-alert v-if="errorMessage" show variant="danger" class="mb-1 py-75 px-1" dismissible @dismissed="errorMessage=null">
              <feather-icon icon="AlertTriangleIcon" size="14" class="mr-50"/>
              <small class="font-weight-bold">{{ errorMessage }}</small>
            </b-alert>

            <b-button
                block
                variant="primary"
                class="mt-1 btn-invoice-action btn-invoice-primary"
                :disabled="loading.action || invoice.items.length === 0 || !invoice.invoiceTitle"
                @click="generateInvoice"
            >
              <b-spinner small type="grow" v-if="loading.action" class="mr-50"/>
              <feather-icon v-else icon="SaveIcon" size="15" class="mr-50"/>
              {{ saveButtonLabel }}
            </b-button>

            <!-- Delete: when editing existing draft (admin only) -->
            <b-button
                v-if="isAdmin && invoiceId != null && isDraft"
                block
                variant="outline-danger"
                size="sm"
                class="mt-75 btn-invoice-action"
                :disabled="loading.action"
                @click="tryDeleteInvoice"
            >
              <feather-icon icon="Trash2Icon" size="13" class="mr-25"/>
              {{ msg('Delete invoice') }}
            </b-button>
          </div>
        </div>

        <!-- ── STEP 2: Confirm / Review ── -->
        <div v-show="step === STEPS.CONFIRM">
          <div class="step-section-header">
            <feather-icon icon="ClipboardIcon" size="18" class="mr-75 text-info"/>
            <span>{{ msg('Review invoice before sending') }}</span>
          </div>
          <div class="p-2">
            <div v-if="invoiceDetails != null">
              <div class="invoice-preview-header mb-1">
                <h5 class="mb-0 font-weight-bolder">{{ invoice.invoiceTitle }}</h5>
                <b-badge :variant="statusVariant(invoiceStatus)" class="px-1">
                  {{ invoiceStatus }}
                </b-badge>
              </div>

              <b-table-simple small responsive class="invoice-preview-table">
                <b-thead>
                  <b-tr>
                    <b-th>{{ msg('Item') }}</b-th>
                    <b-th class="text-center" style="width:70px">{{ msg('Qty') }}</b-th>
                    <b-th class="text-right" style="width:90px">{{ msg('Price') }}</b-th>
                    <b-th class="text-right" style="width:90px">{{ msg('Total') }}</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr v-for="item in invoiceDetails.items" :key="item.externalId || item.name">
                    <b-td class="font-weight-bold">{{ item.name }}</b-td>
                    <b-td class="text-center">{{ item.quantity }}</b-td>
                    <b-td class="text-right">{{ formatAmount(item.price) }}</b-td>
                    <b-td class="text-right font-weight-bold">{{ formatAmount(item.total) }}</b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>

              <div class="invoice-totals">
                <div v-if="invoiceDetails.taxAmount" class="totals-row">
                  <span class="text-muted">{{ msg('Tax') }}</span>
                  <span>{{ formatAmount(invoiceDetails.taxAmount) }}</span>
                </div>
                <div class="totals-row totals-total">
                  <span class="font-weight-bolder">{{ msg('Total') }}</span>
                  <span class="font-weight-bolder text-primary h5 mb-0">{{ formatAmount(invoiceDetails.amount) }}</span>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="invoice-actions mt-2">

              <!-- OPEN / DRAFT: Edit (new link) + Mark as Paid + PDF / Payment link -->
              <template v-if="true">
                <b-row v-if="isAdmin" class="mb-75 no-gutters" style="gap:10px">
                  <b-col cols="6">
                    <b-button block variant="outline-primary" @click="step = STEPS.SET_ITEMS" class="btn-invoice-action">
                      <feather-icon icon="EditIcon" size="14" class="mr-50"/>
                      {{ msg('Edit (new link)') }}
                    </b-button>
                  </b-col>
                  <b-col cols="6">
                    <b-button block variant="success" :disabled="loading.action" @click="tryMarkAsPaid" class="btn-invoice-action btn-invoice-success">
                      <b-spinner small v-if="loading.action" class="mr-50"/>
                      <feather-icon v-else icon="CheckCircleIcon" size="15" class="mr-50"/>
                      {{ msg('Mark as Paid') }}
                    </b-button>
                  </b-col>
                </b-row>
                <b-row class="mb-75 no-gutters" style="gap:10px">
                  <b-col cols="6">
                    <b-button
                        block
                        variant="outline-secondary"
                        size="sm"
                        class="btn-invoice-action"
                        :disabled="!invoiceDetails || !invoiceDetails.links || !invoiceDetails.links.pdfLink"
                        @click="invoiceDetails && openLink(invoiceDetails.links.pdfLink)"
                    >
                      <feather-icon icon="DownloadIcon" size="13" class="mr-25"/>
                      {{ msg('Download PDF') }}
                    </b-button>
                  </b-col>
                  <b-col v-if="invoiceStatus === 'OPEN'" cols="6">
                    <b-button
                        block
                        variant="outline-info"
                        size="sm"
                        class="btn-invoice-action"
                        :disabled="loading.action || !invoiceDetails"
                        @click="openPaymentPage"
                    >
                      <b-spinner v-if="loading.action" small class="mr-50"/>
                      <feather-icon v-else icon="ExternalLinkIcon" size="13" class="mr-25"/>
                      {{ msg('Payment Link') }}
                    </b-button>
                  </b-col>
                </b-row>
              </template>

              <!-- Delete (admin only) -->
              <b-button
                  v-if="isAdmin"
                  block
                  variant="outline-danger"
                  size="sm"
                  class="btn-invoice-action"
                  :disabled="loading.action"
                  @click="tryDeleteInvoice"
              >
                <feather-icon icon="Trash2Icon" size="13" class="mr-25"/>
                {{ msg('Delete invoice') }}
              </b-button>
            </div>
          </div>
        </div>

        <!-- ── STEP 3: Completed ── -->
        <div v-show="step === STEPS.COMPLETED">
          <div class="step-section-header step-section-header--success">
            <feather-icon icon="CheckCircleIcon" size="18" class="mr-75 text-success"/>
            <span>{{ msg('Invoice created successfully') }}</span>
          </div>
          <div class="p-2">
            <div v-if="invoiceDetails != null">
              <div class="invoice-preview-header mb-1">
                <h5 class="mb-0 font-weight-bolder">{{ invoice.invoiceTitle }}</h5>
                <b-badge :variant="statusVariant(invoiceStatus)" class="px-1">
                  {{ invoiceStatus }}
                </b-badge>
              </div>

              <b-table-simple small responsive class="invoice-preview-table">
                <b-thead>
                  <b-tr>
                    <b-th>{{ msg('Item') }}</b-th>
                    <b-th class="text-center" style="width:70px">{{ msg('Qty') }}</b-th>
                    <b-th class="text-right" style="width:90px">{{ msg('Price') }}</b-th>
                    <b-th class="text-right" style="width:90px">{{ msg('Total') }}</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr v-for="item in invoiceDetails.items" :key="item.externalId || item.name">
                    <b-td class="font-weight-bold">{{ item.name }}</b-td>
                    <b-td class="text-center">{{ item.quantity }}</b-td>
                    <b-td class="text-right">{{ formatAmount(item.price) }}</b-td>
                    <b-td class="text-right font-weight-bold">{{ formatAmount(item.total) }}</b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>

              <div class="invoice-totals">
                <div v-if="invoiceDetails.taxAmount" class="totals-row">
                  <span class="text-muted">{{ msg('Tax') }}</span>
                  <span>{{ formatAmount(invoiceDetails.taxAmount) }}</span>
                </div>
                <div class="totals-row totals-total">
                  <span class="font-weight-bolder">{{ msg('Total') }}</span>
                  <span class="font-weight-bolder text-success h5 mb-0">{{ formatAmount(invoiceDetails.amount) }}</span>
                </div>
              </div>

              <!-- Quick links -->
              <div class="invoice-links mt-1">
                <b-button
                    v-if="invoiceDetails.links && invoiceDetails.links.pdfLink"
                    variant="outline-secondary"
                    size="sm"
                    class="mr-1"
                    @click="openLink(invoiceDetails.links.pdfLink)"
                >
                  <feather-icon icon="DownloadIcon" size="13" class="mr-25"/>
                  {{ msg('Download PDF') }}
                </b-button>
                <b-button
                    v-if="invoiceStatus === 'OPEN'"
                    variant="outline-info"
                    size="sm"
                    :disabled="loading.action || !invoiceDetails"
                    @click="openPaymentPage"
                >
                  <b-spinner v-if="loading.action" small class="mr-50"/>
                  <feather-icon v-else icon="ExternalLinkIcon" size="13" class="mr-25"/>
                  {{ msg('Payment link') }}
                </b-button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </b-modal>
  </div>
</template>

<script>
import { BuildingInvoiceRequest, BuildingInvoiceItem, BuildInvoiceRequestFromInvoice } from '@/model/RealEstate/invoice/BuildInvoiceRequest'
import draggable from 'vuedraggable'
import { BuildingExpensesModel } from '@/model/RealEstate/BuildingModel'
import { mapActions, mapGetters } from 'vuex'

const STEPS = { SET_EXPENSES: 0, SET_ITEMS: 1, CONFIRM: 2, COMPLETED: 3 }

export default {
  name: 'BuildingInvoiceModal',
  components: { draggable },
  props: {
    building: {
      type: Object,
    },
  },
  data: () => ({
    STEPS,
    loading: {
      invoice: false,
      action: false,
    },
    taxes: [],
    drag: false,
    step: STEPS.SET_EXPENSES,
    expenseId: null,
    useCustomPeriod: false,
    customMonth: null,
    customYear: null,
    selectedTax: null,
    invoiceId: null,
    invoiceStatus: null,
    invoiceDetails: null,
    currentBuildingId: null,
    invoice: BuildingInvoiceRequest(),
    errorMessage: null,
  }),
  created() {
    this.getTaxes().then(taxes => { this.taxes = taxes })
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    ...mapGetters('user', ['myAdminStatus']),

    isAdmin() {
      return true
    },

    stepLabels() {
      return [
        this.msg('Expense'),
        this.msg('Items'),
        this.msg('Review'),
        this.msg('Done'),
      ]
    },

    modalTitle() {
      const labels = [
        this.msg('Select Expense'),
        this.msg('Configure Items'),
        this.msg('Review Invoice'),
        this.msg('Invoice Created'),
      ]
      return labels[this.step] || this.msg('Invoice')
    },

    /** Month/year keys for periods that already have a PAID invoice (not selectable as expense period). */
    paidInvoicePeriodKeys() {
      const keys = new Set()
      for (const inv of this.building && this.building.buildingInvoices || []) {
        if (String(inv.invoiceStatus || '').toUpperCase() !== 'PAID') continue
        const p = this.periodFromBuildingInvoice(inv)
        if (p) keys.add(`${p.month}-${p.year}`)
      }
      return keys
    },

    /** All building expense periods; periods with a paid invoice are disabled (not selectable). */
    expensesOptions() {
      const expenses = (this.building && this.building.buildingExpenses) || []
      const paidKeys = this.paidInvoicePeriodKeys
      return expenses.map((expense) => {
        const m = parseInt(String(expense.month), 10)
        const y = parseInt(String(expense.year), 10)
        const paid = !Number.isNaN(m) && !Number.isNaN(y) && paidKeys.has(`${m}-${y}`)
        return {
          value: expense.id,
          text: paid
            ? `${expense.month}/${expense.year} — ${this.msg('Paid')}`
            : `${expense.month}/${expense.year}`,
          disabled: paid,
        }
      })
    },

    customMonthOptions() {
      const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return [
        { value: null, text: this.msg('Month') },
        ...labels.map((l, i) => ({ value: i + 1, text: l })),
      ]
    },

    customYearOptions() {
      const y = new Date().getFullYear()
      const opts = [{ value: null, text: this.msg('Year') }]
      for (let yy = y + 1; yy >= y - 15; yy--) {
        opts.push({ value: yy, text: String(yy) })
      }
      return opts
    },

    canContinueFromExpenseStep() {
      if (this.useCustomPeriod) {
        return this.customMonth != null && this.customYear != null
      }
      if (!this.expenseId) return false
      const opt = this.expensesOptions.find((o) => o.value === this.expenseId)
      return !!(opt && !opt.disabled)
    },

    expensesEmptyMessage() {
      return this.msg('No expense periods are recorded yet. Use Custom month / year or add building expenses first.')
    },

    taxesOptions() {
      return [
        { value: null, text: this.msg('No tax') },
        ...(this.taxes || []).map(tax => ({
          value: tax.id,
          text: `${tax.displayName} — ${tax.country} ${tax.rate}%`,
        })),
      ]
    },

    isDraft() {
      return this.invoiceStatus == null || this.invoiceStatus === 'DRAFT'
    },

    saveButtonLabel() {
      if (this.invoiceId == null) return this.msg('Create invoice')
      if (this.invoiceStatus === 'OPEN') return this.msg('Create new invoice (new link)')
      return this.msg('Save')
    },

    /** Always create new: either brand-new or replacing an OPEN invoice. */
    saveAsNewInvoice() {
      return this.invoiceId == null || this.invoiceStatus === 'OPEN'
    },

    itemsSubtotal() {
      return (this.invoice.items || []).reduce((sum, item) => {
        return sum + (parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0)
      }, 0)
    },
  },
  methods: {
    ...mapActions('building', [
      'retrieveInvoice',
      'createDraftInvoice',
      'updateDraftInvoice',
      'deleteInvoice',
      'markInvoiceAsPaid',
    ]),
    ...mapActions('tax', ['getTaxes']),

    /**
     * Maps a building invoice snapshot to calendar month/year (expense period).
     * Prefer title "Invoice M/YYYY" (scheduler / default); else invoice creation date.
     */
    periodFromBuildingInvoice(inv) {
      if (!inv) return null
      const title = inv.title || ''
      const match = title.match(/invoice\s+(\d{1,2})\s*\/\s*(\d{4})/i)
      if (match) {
        const month = parseInt(match[1], 10)
        const year = parseInt(match[2], 10)
        if (!Number.isNaN(month) && !Number.isNaN(year)) return { month, year }
      }
      if (inv.creationDate) {
        const d = new Date(inv.creationDate)
        if (!Number.isNaN(d.getTime())) {
          return { month: d.getMonth() + 1, year: d.getFullYear() }
        }
      }
      return null
    },

    expensePeriodSelectable(option) {
      return !!(option && !option.disabled)
    },

    onExpensePeriodInput(val) {
      if (val) this.useCustomPeriod = false
    },

    onCustomPeriodToggle() {
      this.$nextTick(() => {
        if (this.useCustomPeriod) this.expenseId = null
      })
    },

    showModal(buildingId = null) {
      console.log('buildingId in showModal', buildingId)
      this.currentBuildingId = buildingId || (this.building && this.building.id)
      this.step = STEPS.SET_EXPENSES
      this.invoiceDetails = null
      this.invoiceId = null
      this.invoiceStatus = null
      this.expenseId = null
      this.useCustomPeriod = false
      this.customMonth = null
      this.customYear = null
      this.selectedTax = null
      this.errorMessage = null
      this.invoice = BuildingInvoiceRequest({ buildId: this.currentBuildingId })
      this.$nextTick(() => {
        if (this.$refs.invoiceModal) this.$refs.invoiceModal.show()
      })
    },

    showInvoice(invoiceId) {
      this.loading.invoice = true
      this.invoiceDetails = null
      this.$nextTick(() => {
        if (this.$refs.invoiceModal) this.$refs.invoiceModal.show()
      })
      this.retrieveInvoice(invoiceId).then(({ invoice, invoiceDetails }) => {
        // invoiceDetails is the full Invoice entity which has baseId (building ID)
        this.currentBuildingId = (invoiceDetails && invoiceDetails.baseId) ||
                                  (this.building && this.building.id)
        this.invoice = BuildInvoiceRequestFromInvoice(invoice, invoiceDetails)
        // Ensure buildId is always set on the invoice object
        this.invoice.buildId = this.currentBuildingId
        this.invoiceDetails = invoiceDetails
        this.invoiceId = invoiceId
        this.invoiceStatus = invoice.invoiceStatus
        if (invoice.invoiceStatus === 'PAID' || invoice.invoiceStatus === 'CANCELED' || invoice.invoiceStatus === 'VOIDED') {
          this.step = STEPS.COMPLETED
        } else if (invoice.invoiceStatus === 'DRAFT') {
          this.step = STEPS.SET_ITEMS
        } else {
          this.step = STEPS.CONFIRM
        }
      }).finally(() => {
        this.loading.invoice = false
      })
    },

    setItemsByExpenses() {
      if (this.useCustomPeriod) {
        if (this.customMonth == null || this.customYear == null) return
        this.invoice.items = []
        this.invoice.invoiceTitle = `Invoice ${this.customMonth}/${this.customYear}`
        this.step = STEPS.SET_ITEMS
        return
      }
      const selectedExpense = (this.building && this.building.buildingExpenses || [])
          .find(es => es.id === this.expenseId)
      if (selectedExpense != null) {
        this.invoice.items = []
        this.generateItems(selectedExpense, this.invoice.items)
        this.invoice.invoiceTitle = `Invoice ${selectedExpense.month}/${selectedExpense.year}`
      }
      this.step = STEPS.SET_ITEMS
    },

    generateItems(expenses, items) {
      const EXPENSE_FIELDS = ['rent','internet','insurance','damage','gas','electricity','cleaning','commission','management','keybox']
      const selectedExpense = BuildingExpensesModel(expenses || {})
      EXPENSE_FIELDS.forEach(key => {
        const val = parseFloat(selectedExpense[key]) || 0
        if (val !== 0) {
          items.push(BuildingInvoiceItem({ name: key, quantity: 1, price: Math.abs(val) }))
        }
      })
    },

    addItem() {
      this.invoice.items.push(BuildingInvoiceItem({ name: '', quantity: 1, price: 0 }))
    },

    generateInvoice() {
      this.loading.action = true
      this.errorMessage = null
      const payload = {
        ...this.invoice,
        buildId: this.currentBuildingId,
        taxRateIds: this.selectedTax != null ? [this.selectedTax] : [],
      }
      if (this.saveAsNewInvoice && this.invoiceId && this.invoiceStatus === 'OPEN') {
        payload.replacesInvoiceId = this.invoiceId
      }
      const request = this.saveAsNewInvoice
        ? this.createDraftInvoice(payload)
        : this.updateDraftInvoice({ id: this.invoiceId, invoice: payload })

      request.then(({ invoice, invoiceDetails }) => {
        this.invoiceId = invoiceDetails.id
        this.invoiceDetails = invoiceDetails
        this.invoice = BuildInvoiceRequestFromInvoice(invoice, invoiceDetails)
        this.invoice.buildId = this.currentBuildingId
        this.invoiceStatus = invoice.invoiceStatus
        this.step = invoice.invoiceStatus === 'OPEN' ? STEPS.COMPLETED : STEPS.CONFIRM
        this.$emit('invoice', invoice)
      }).catch((err) => {
        this.errorMessage = (typeof err === 'object' && err && err.message) ? err.message : String(err || 'Unknown error')
      }).finally(() => {
        this.loading.action = false
      })
    },

    tryDeleteInvoice() {
      this.loading.action = true
      this.deleteInvoice(this.invoiceId).then(() => {
        this.$emit('invoice', null)
        if (this.$refs.invoiceModal) this.$refs.invoiceModal.hide()
      }).finally(() => {
        this.loading.action = false
      })
    },

    tryMarkAsPaid() {
      this.loading.action = true
      this.markInvoiceAsPaid(this.invoiceId).then(() => {
        this.invoiceStatus = 'PAID'
        this.step = STEPS.COMPLETED
        this.$emit('invoice', { invoiceId: this.invoiceId, invoiceStatus: 'PAID' })
      }).finally(() => {
        this.loading.action = false
      })
    },

    formatAmount(value) {
      if (value == null || value === '') return '—'
      const n = parseFloat(value)
      if (isNaN(n)) return '—'
      return `€${n.toFixed(2)}`
    },

    statusVariant(status) {
      const map = { DRAFT: 'secondary', OPEN: 'warning', PAID: 'success', VOID: 'danger', DELETED: 'danger' }
      return map[(status || '').toUpperCase()] || 'secondary'
    },

    openLink(url) {
      if (url) window.open(url, '_blank')
    },

    /** Open Stripe hosted invoice; re-fetch if link missing (retrieve triggers Stripe refresh for OPEN invoices). */
    async openPaymentPage() {
      if (!this.invoiceId) return
      if (this.invoiceDetails && this.invoiceDetails.links && this.invoiceDetails.links.paymentLink) {
        this.openLink(this.invoiceDetails.links.paymentLink)
        return
      }
      this.loading.action = true
      try {
        const { invoice, invoiceDetails } = await this.retrieveInvoice(this.invoiceId)
        this.invoiceDetails = invoiceDetails
        this.invoice = BuildInvoiceRequestFromInvoice(invoice, invoiceDetails)
        this.invoice.buildId = this.currentBuildingId
        this.invoiceStatus = invoice.invoiceStatus
        if (this.invoiceDetails.links && this.invoiceDetails.links.paymentLink) {
          this.openLink(this.invoiceDetails.links.paymentLink)
        }
      } finally {
        this.loading.action = false
      }
    },
  },
}
</script>

<style scoped>
/* ── Progress stepper ── */
.invoice-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 1.5rem 0.75rem;
  position: relative;
  gap: 0;
}

.progress-line {
  position: absolute;
  top: 50%;
  left: 2rem;
  right: 2rem;
  height: 2px;
  background: #e0e0e0;
  z-index: 0;
  margin-top: -6px;
}

.invoice-progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
  z-index: 1;
}

.step-bubble {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 700;
  border: 2px solid #e0e0e0;
  background: #fff;
  color: #adb5bd;
  transition: all 0.2s;
}

.step-active .step-bubble {
  border-color: #7367f0;
  background: #7367f0;
  color: #fff;
  box-shadow: 0 0 0 4px rgba(115, 103, 240, 0.15);
}

.step-done .step-bubble {
  border-color: #28c76f;
  background: #28c76f;
  color: #fff;
}

.step-label {
  font-size: 0.7rem;
  margin-top: 4px;
  color: #adb5bd;
  font-weight: 500;
}

.step-active .step-label {
  color: #7367f0;
  font-weight: 700;
}

.step-done .step-label {
  color: #28c76f;
}

/* ── Section header ── */
.step-section-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  background: rgba(115, 103, 240, 0.03);
  font-size: 0.875rem;
  font-weight: 600;
  color: #5e5873;
}

.step-section-header--success {
  background: rgba(40, 199, 111, 0.04);
}

/* ── Loading ── */
.invoice-loading {
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.invoice-body {
  min-height: 200px;
}

/* ── Info hint ── */
.info-hint {
  background: rgba(0, 149, 255, 0.05) !important;
  border: 1px solid rgba(0, 149, 255, 0.18) !important;
  border-radius: 4px;
}

/* ── Items table ── */
.items-table-wrapper {
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 6px;
  overflow: hidden;
}

.items-table-head {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: rgba(0,0,0,0.03);
  border-bottom: 1px solid rgba(0,0,0,0.06);
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6e6b7b;
}

.item-row {
  display: flex;
  align-items: center;
  padding: 0.35rem 0.5rem;
  border-bottom: 1px solid rgba(0,0,0,0.04);
  gap: 6px;
}

.item-row:last-child {
  border-bottom: none;
}

.drag-handle {
  cursor: grab;
  padding: 0 4px;
  opacity: 0.4;
  flex-shrink: 0;
}

.drag-handle:hover { opacity: 0.8; }

.col-name  { flex: 1; min-width: 0; }
.col-qty   { width: 64px; flex-shrink: 0; }
.col-price { width: 80px; flex-shrink: 0; }
.col-total { width: 72px; flex-shrink: 0; font-size: 0.82rem; color: #5e5873; }
.col-del   { width: 32px; flex-shrink: 0; }

.items-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem;
  color: #adb5bd;
}

.items-add-row {
  padding: 0.4rem 0.6rem;
  border-top: 1px dashed rgba(0,0,0,0.08);
}

.items-subtotal {
  text-align: right;
  padding: 0.4rem 0.5rem;
  font-size: 0.85rem;
}

/* ── Invoice preview ── */
.invoice-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.invoice-preview-table {
  font-size: 0.875rem;
  margin-bottom: 0;
}

.invoice-preview-table thead th {
  background: rgba(0,0,0,0.02);
  border-top: none;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6e6b7b;
}

.invoice-totals {
  border-top: 2px solid rgba(0,0,0,0.07);
  padding-top: 0.5rem;
  margin-top: 0;
}

.totals-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.2rem 0;
  font-size: 0.875rem;
}

.totals-total {
  border-top: 1px solid rgba(0,0,0,0.06);
  padding-top: 0.4rem;
  margin-top: 0.2rem;
}

.invoice-links {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

/* ── Action buttons ── */
.btn-invoice-action {
  border-radius: 6px;
  font-weight: 600;
  padding: 0.5rem 1rem;
  transition: box-shadow 0.15s ease, transform 0.1s ease;
}
.btn-invoice-action:hover:not(:disabled) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.btn-invoice-primary {
  padding: 0.6rem 1.25rem;
  font-size: 0.9375rem;
}
.btn-invoice-success {
  padding: 0.55rem 1rem;
}
.invoice-actions .btn {
  margin-bottom: 0.5rem;
}
.invoice-actions .btn:last-child {
  margin-bottom: 0;
}
</style>

<style>
/* Dark mode */
.dark-layout .invoice-modal-content {
  background-color: #283046;
}

.dark-layout .invoice-modal-header {
  background-color: #283046;
  border-bottom-color: rgba(255,255,255,0.06);
  color: #d0d2d6;
}

.dark-layout .progress-line {
  background: rgba(255,255,255,0.1);
}

.dark-layout .step-bubble {
  background: #283046;
  border-color: rgba(255,255,255,0.12);
  color: #676d7d;
}

.dark-layout .step-section-header {
  background: rgba(115, 103, 240, 0.06);
  border-bottom-color: rgba(255,255,255,0.05);
  color: #b4b7bd;
}

.dark-layout .step-section-header--success {
  background: rgba(40, 199, 111, 0.06);
}

.dark-layout .items-table-wrapper {
  border-color: rgba(255,255,255,0.07);
}

.dark-layout .items-table-head {
  background: rgba(255,255,255,0.03);
  border-bottom-color: rgba(255,255,255,0.05);
  color: #676d7d;
}

.dark-layout .item-row {
  border-bottom-color: rgba(255,255,255,0.04);
}

.dark-layout .items-add-row {
  border-top-color: rgba(255,255,255,0.07);
}

.dark-layout .invoice-preview-table thead th {
  background: rgba(255,255,255,0.03);
  color: #676d7d;
}

.dark-layout .invoice-preview-table tbody td {
  color: #b4b7bd;
  border-top-color: rgba(255,255,255,0.04);
}

.dark-layout .invoice-totals {
  border-top-color: rgba(255,255,255,0.08);
}

.dark-layout .totals-total {
  border-top-color: rgba(255,255,255,0.06);
}

.dark-layout .totals-row span {
  color: #b4b7bd;
}

.dark-layout .info-hint {
  background: rgba(0,149,255,0.07) !important;
  border-color: rgba(0,149,255,0.2) !important;
}

.dark-layout .items-subtotal {
  color: #b4b7bd;
}
</style>
